"use client";

import * as SelectPrimitive from "@radix-ui/react-select";
import { ArrowDown, Check, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@drpcrd/utils/shared";
import {
  ComponentPropsWithoutRef,
  ComponentRef,
  FC,
  forwardRef,
  useState,
} from "react";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = forwardRef<
  ComponentRef<typeof SelectPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & { open?: boolean }
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "group flex w-full cursor-pointer items-center justify-between rounded-md border border-neutral-500 bg-white p-1.5 text-lg leading-2.75 placeholder:text-slate-500 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon
      asChild
      className="group-data-[state=open]:border-primary group-data-[state=open]:bg-primary relative ml-1 flex min-h-[20px] min-w-[20px] items-center justify-center rounded-full border border-neutral-500 bg-white p-[3px] text-center align-middle group-hover:bg-neutral-500"
    >
      <ArrowDown className="h-1 w-1 [&_path]:group-data-[state=open]:stroke-white" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = forwardRef<
  ComponentRef<typeof SelectPrimitive.ScrollUpButton>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronUp className="h-3 w-3" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = forwardRef<
  ComponentRef<typeof SelectPrimitive.ScrollDownButton>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronDown className="h-3 w-3" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = forwardRef<
  ComponentRef<typeof SelectPrimitive.Content>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-[var(--radix-select-content-available-height)] min-w-[8rem] overflow-hidden rounded-md border border-neutral-900 bg-white text-slate-950 shadow-md",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-0",
          position === "popper" &&
            "max-h-60 w-full min-w-[var(--radix-select-trigger-width)] overflow-y-auto",
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = forwardRef<
  ComponentRef<typeof SelectPrimitive.Label>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("py-1.5 pr-2 pl-8 text-sm font-semibold", className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = forwardRef<
  ComponentRef<typeof SelectPrimitive.Item>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "focus:bg-primary-50 relative flex w-full cursor-pointer items-center rounded-md border-transparent py-1.5 pr-6 pl-2 text-sm leading-2.75 text-nowrap outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50",
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>

    <span className="border-primary absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-3 w-3" />
      </SelectPrimitive.ItemIndicator>
    </span>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = forwardRef<
  ComponentRef<typeof SelectPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-slate-100", className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

interface StyledSelectProps {
  defaultValue: string;
  onValueChange: (value: string) => void;
  options: { value: string; label: string }[];
  label: string;
  value?: string;
  loading?: boolean;
}

const StyledSelect: FC<StyledSelectProps> = ({
  defaultValue,
  onValueChange,
  options,
  label,
  value,
  loading = false,
}) => {
  const [open, setOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center">
        <div className="mr-[5px] hidden h-3 w-14 animate-pulse rounded-sm bg-slate-200 md:inline" />
        <div className="h-3 w-3 animate-pulse rounded-sm bg-slate-200" />
      </div>
    );
  }

  return (
    <Select
      onValueChange={onValueChange}
      defaultValue={defaultValue}
      value={value}
      open={open}
      onOpenChange={setOpen}
    >
      <SelectTrigger
        className="mt-0 w-auto cursor-pointer border-none bg-transparent px-0 py-0 text-sm font-semibold ring-0 ring-offset-0 outline-hidden focus:border-neutral-100 focus:ring-neutral-100 focus:ring-offset-0 focus:outline-hidden"
        open={open}
      >
        <span className="mr-[5px] hidden font-normal md:inline">{label}</span>
        <SelectValue />
      </SelectTrigger>

      <SelectContent
        position="popper"
        side="bottom"
        align="end"
        className="shadow-dropdown mt-0 w-full rounded-xl px-1 pb-1 font-semibold"
      >
        <h1 className="px-1.5 py-2 text-sm font-semibold">{label}</h1>
        <div className="mx-1 mb-1 h-[1px] w-full bg-neutral-500"></div>
        {options.map((item) => (
          <SelectItem
            key={item.value}
            value={item.value}
            className="cursor-pointer rounded-lg py-1 hover:data-[state=checked]:bg-transparent"
          >
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export {
  StyledSelect,
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
